import { Currency } from "gen/clients";

/**
 * Formats a price value with the appropriate currency symbol and formatting
 * @param price - The price value to be formatted
 * @param currency - The currency code (USD, EUR, GBP)
 * @returns A string representing the formatted price with currency symbol
 */
export function formatPriceWithCurrency(price: number, currency?: Currency | string): string {
  const currencyCode = currency || "USD";

  // Map currency codes to their respective locales for proper formatting
  const currencyLocaleMap: Record<string, string> = {
    USD: "en-US",
    EUR: "de-DE", // Using German locale for EUR formatting
    GBP: "en-GB"
  };

  const locale = currencyLocaleMap[currencyCode] || "en-US";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(price);
}

/**
 * Gets the currency symbol for a given currency code
 * @param currency - The currency code
 * @returns The currency symbol
 */
export function getCurrencySymbol(currency?: Currency | string): string {
  const currencyCode = currency || "USD";

  const symbolMap: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£"
  };

  return symbolMap[currencyCode] || "$";
}

/**
 * Gets the currency name for display in helper text
 * @param currency - The currency code
 * @returns The currency name
 */
export function getCurrencyName(currency?: Currency | string): string {
  const currencyCode = currency || "USD";

  const nameMap: Record<string, string> = {
    USD: "US Dollars",
    EUR: "Euros",
    GBP: "British Pounds"
  };

  return nameMap[currencyCode] || "US Dollars";
}
